import {
  createContext,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

type PopupPosition = {
  left: number;
  top: number;
  targetRect?: DOMRect;
  viewportWidth?: number;
  viewportHeight?: number;
  scrollX?: number;
  scrollY?: number;
};

export function usePopup() {
  const [content, setContent] = useState<ReactNode | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [position, setPosition] = useState<PopupPosition>({ left: 0, top: 0 });
  const pos = useRef<PopupPosition>({ left: 0, top: 0 });
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    console.log("Closing popup");
    setIsOpen(false);

    // Delay content cleanup to avoid unmounting issues
    setTimeout(() => {
      setContent(null);
      setSize({ width: 0, height: 0 });

      // Remove the mask element if it exists
      const mask = document.querySelector("#app-popup-mask");
      if (mask && mask.parentNode) {
        try {
          mask.remove();
        } catch (error) {
          console.warn("Error removing popup mask:", error);
        }
      }
    }, 0);
  }, []);

  // Add escape key handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        close();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, close]);

  const setPopup = useCallback(
    (
      e:
        | HTMLElement
        | React.MouseEvent<HTMLElement>
        | React.KeyboardEvent<HTMLElement>,
      content_: typeof content
    ) => {
      console.log("setPopup called with:", {
        event: e,
        eventType: e && "type" in e ? e.type : "HTMLElement",
        content: content_,
      });

      if (!e) {
        console.error("setPopup received null/undefined event");
        return;
      }

      setContent(content_);
      let left = 0;
      let top = 0;

      try {
        if (isMouseEvent(e)) {
          console.log("Handling mouse event");
          const target = e.target as HTMLElement;
          if (!target) {
            console.error("Mouse event has no target");
            return;
          }
          const rect = target.getBoundingClientRect();
          const scrollY = window.scrollY || document.documentElement.scrollTop;
          const scrollX = window.scrollX || document.documentElement.scrollLeft;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          left = rect.left + scrollX;
          top = rect.top + rect.height + scrollY;

          pos.current = {
            left,
            top,
            targetRect: rect,
            viewportWidth,
            viewportHeight,
            scrollX,
            scrollY,
          };
        } else {
          console.log("Handling keyboard/element event");
          const target = isKeyboardEvent(e) ? (e.target as HTMLElement) : e;
          if (!target) {
            console.error("No valid target element found");
            return;
          }
          const rect = target.getBoundingClientRect();
          const scrollY = window.scrollY || document.documentElement.scrollTop;
          const scrollX = window.scrollX || document.documentElement.scrollLeft;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          left = rect.left + scrollX;
          top = rect.top + rect.height + scrollY;

          pos.current = {
            left,
            top,
            targetRect: rect,
            viewportWidth,
            viewportHeight,
            scrollX,
            scrollY,
          };
        }

        console.log("Setting popup position:", { left, top });
        setPosition({ left, top });
        setIsOpen(true);
      } catch (error) {
        console.error("Error in setPopup:", error);
        // Clean up if there's an error
        setContent(null);
        setIsOpen(false);
      }
    },
    []
  );
  useEffect(() => {
    if (!isOpen) {
      setContent(null);
      setSize({ width: 0, height: 0 });
      // Remove the mask element
      const mask = document.querySelector("#app-popup-mask");
      if (mask) {
        mask.remove();
      }
    }
  }, [isOpen]);

  const resize = useCallback(() => {
    if (isOpen) {
      const el = document.querySelector(
        "#app-popup-container"
      )?.firstElementChild;
      if (!el) return;

      const { width, height } = el.getBoundingClientRect();
      setSize({ width, height });

      // Get stored position and viewport info
      const {
        left: initialLeft,
        top: initialTop,
        targetRect,
        viewportWidth = window.innerWidth,
        viewportHeight = window.innerHeight,
        scrollX = window.scrollX || document.documentElement.scrollLeft,
        scrollY = window.scrollY || document.documentElement.scrollTop,
      } = pos.current;

      if (!targetRect) return;

      // Calculate bounds
      let left = initialLeft;
      let top = initialTop;

      // Adjust horizontal position if needed
      if (left + width > viewportWidth + scrollX - 20) {
        left = viewportWidth + scrollX - width - 20;
      }
      if (left < scrollX + 20) {
        left = scrollX + 20;
      }

      // Adjust vertical position if needed
      const bottomSpace = viewportHeight + scrollY - initialTop;
      const topSpace = targetRect.top + scrollY - scrollY;

      if (height > bottomSpace && topSpace > bottomSpace) {
        // If not enough space below but enough space above, flip to above
        top = targetRect.top + scrollY - height - 8;
      } else if (top + height > viewportHeight + scrollY - 20) {
        // If still not enough space, align to bottom of viewport
        top = viewportHeight + scrollY - height - 20;
      }
      if (top < scrollY + 20) {
        top = scrollY + 20;
      }

      // Update position if changed
      if (left !== pos.current.left || top !== pos.current.top) {
        setPosition({ left, top });
        pos.current = { ...pos.current, left, top };
      }
    }
  }, [isOpen]);

  useLayoutEffect(() => resize(), [resize]);

  return {
    content,
    size,
    position,
    isOpen,
    open,
    close,
    setPopup,
    resize,
  };
}

export const PopupContext = createContext<ReturnType<typeof usePopup>>({
  open() {},
  close() {},
  setPopup() {},
  resize() {},
  isOpen: false,
  content: null,
  size: { width: 0, height: 0 },
  position: { left: 0, top: 0 },
});

export function usePopupContext() {
  return useContext(PopupContext);
}

function isMouseEvent(
  e:
    | HTMLElement
    | React.MouseEvent<HTMLElement>
    | React.KeyboardEvent<HTMLElement>
): e is React.MouseEvent<HTMLElement> {
  if (!e) {
    console.warn("isMouseEvent received null/undefined event");
    return false;
  }

  // Check if it's a DOM element
  if (e instanceof HTMLElement) {
    return false;
  }

  // Check if it's a synthetic event
  if ("type" in e) {
    return ["click", "mousedown", "mouseup", "mousemove"].includes(e.type);
  }

  console.warn("isMouseEvent received unknown event type:", e);
  return false;
}

function isKeyboardEvent(
  e:
    | HTMLElement
    | React.MouseEvent<HTMLElement>
    | React.KeyboardEvent<HTMLElement>
): e is React.KeyboardEvent<HTMLElement> {
  if (!e) {
    console.warn("isKeyboardEvent received null/undefined event");
    return false;
  }

  // Check if it's a DOM element
  if (e instanceof HTMLElement) {
    return false;
  }

  // Check if it's a synthetic event
  if ("type" in e) {
    return e.type?.startsWith("key") || false;
  }

  console.warn("isKeyboardEvent received unknown event type:", e);
  return false;
}
